import { useState, useEffect } from 'react';
import type { MouseEvent, ReactNode } from 'react';

import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';
import { pushToDataLayer } from 'services/gtmService';

import {
  Card,
  CardOrientation,
  CardVariations,
} from 'components/Toolkit/Card/Card';
import { ManufacturerApproved } from 'components/Toolkit/Tags/ManufacturerApproved';
import { CardPaymentTag } from 'components/Toolkit/Tags/PreBuilt/CardPaymentTag';
import { DeliveryAvailableTag } from 'components/Toolkit/Tags/PreBuilt/DeliveryAvailableTag';
import { HistoryCheckTag } from 'components/Toolkit/Tags/PreBuilt/HistoryCheckTag';
import { WarrantyTag } from 'components/Toolkit/Tags/PreBuilt/WarrantyTag';
import { Tag } from 'components/Toolkit/Tags/Tag';
import { GreenLightTag } from 'components/Toolkit/Tags/PreBuilt/GreenlightTag';
import { HerdwatchTag } from 'components/Toolkit/Tags/PreBuilt/HerdwatchTag';
import { useUserContext } from 'contexts/UserContext';
import { accountsApi } from 'api/accountsApi';
import { fireToast } from 'helpers/Toasts';
import { WantedTag } from 'components/Toolkit/Tags/PreBuilt/WantedTag';
import { logBrazeSaveAdEvent } from 'services/brazeService';
import StatusCode from 'status-code-enum';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { IFinancePricePerMonthSearchModel } from 'api/types/accountsApiTypes';
import { formatAuthorizationHeader } from 'helpers/auth';

import type {
  PhotoView,
  DealerSummary,
  IOldPrice,
  ICardPayment,
  IMerits,
} from 'types';
import type { ThemeTypes } from 'types/Theme.namespace';

export interface CadCardProps {
  adId?: number;
  variant: CardVariations;
  orientation: CardOrientation;
  header: string;
  price?: string;
  oldPriceView?: IOldPrice;
  currency?: 'EUR' | 'GBP';
  keyInfo?: string[];
  age?: string;
  county?: string;
  countyTown?: string;
  sectionDisplayName?: string;
  sectionName?: string;
  sellerType?: 'TRADER' | 'PRIVATE' | 'DEALER';
  meritsV2?: IMerits[];
  financeSummary?: IFinancePricePerMonthSearchModel;
  photos?: PhotoView[];
  userSaved?: boolean;
  mediaCount?: number;
  dealer?: DealerSummary;
  manufacturerApprovedBadge?: { badgeUrl?: string; manufacturer?: string };
  wanted?: boolean;
  herdwatchVerified?: boolean;
  specialOffer?: string;
  greenlightVerified?: boolean;
  cardPayments?: ICardPayment[];
  deliveryAvailable?: boolean;
  imageAlt?: string;
  cdnUrl: string;
  id: number;
  /** Manually hide save option */
  hideSave?: boolean;
  isJato?: boolean;
  jatoPayload?: {
    make: string;
    model: string;
    bodyType: string;
  };
  highlight?: ThemeTypes.Colors;
  highlightText?: string;
  trackCreateSaveAd?: (adId: number) => void;
  trackDeleteSaveAd?: () => void;
  isGrid?: boolean;
  isMobile?: boolean;
  priceOnRequest?: boolean;
  renderAdditionalHeaderInfo?: ReactNode;
  renderSubtitle?: ReactNode;
  shouldTruncateHeader?: boolean;
  smallTruncationHeaderWidth?: number;
}

function CadCard({
  adId,
  header,
  oldPriceView,
  keyInfo,
  age,
  county,
  countyTown,
  sectionDisplayName,
  sectionName,
  sellerType,
  financeSummary,
  photos,
  userSaved = false,
  dealer,
  cardPayments,
  deliveryAvailable,
  manufacturerApprovedBadge,
  specialOffer,
  meritsV2,
  wanted,
  greenlightVerified,
  herdwatchVerified,
  cdnUrl,
  id,
  hideSave,
  imageAlt,
  isJato,
  jatoPayload,
  highlight,
  highlightText,
  trackCreateSaveAd,
  trackDeleteSaveAd,
  isGrid = false,
  isMobile = false,
  priceOnRequest = false,
  renderAdditionalHeaderInfo,
  renderSubtitle,
  shouldTruncateHeader = false,
  smallTruncationHeaderWidth = 0,
  ...rest
}: CadCardProps) {
  const { user, isLoginModalOpen } = useUserContext();
  const { handleLogin, accessToken } = useAuthOptions();
  const [queueSaveAd, setQueueSaveAd] = useState<boolean>(false);
  const [isSaved, setIsAdSaved] = useState<boolean>(userSaved);
  const infoItems = [];

  if (financeSummary?.price) {
    infoItems.push({
      title: 'From',
      content: `€${financeSummary.price}`,
      subText: 'p/m',
    });
  }

  useOnUpdateOnly(() => {
    setIsAdSaved(userSaved);
  }, [adId]);

  useEffect(() => {
    if (user?.id && queueSaveAd) {
      triggerToggleSave();
      setQueueSaveAd(false);
    }
    if (!isLoginModalOpen) {
      setQueueSaveAd(false);
    }
  }, [isLoginModalOpen]);

  function onSave(event: MouseEvent) {
    event.preventDefault(); // Do no trigger the link if one is wrapped around this component
    event.stopPropagation(); // Do not trigger the click handler if one is included in the parent component
    user?.id ? triggerToggleSave() : loginAndSave();
  }

  function loginAndSave() {
    setQueueSaveAd(true);
    handleLogin();
  }

  async function triggerToggleSave() {
    const res = isSaved !== undefined && (await handleRequest(isSaved));
    if (res) {
      if (!isSaved) {
        if (trackCreateSaveAd) {
          trackCreateSaveAd(adId ?? 0);
          logBrazeSaveAdEvent(sectionName, sellerType);
        } else {
          pushToDataLayer({
            event: 'create_saved_ad',
          });
          logBrazeSaveAdEvent(sectionName, 'TRADER');
        }
      } else {
        if (trackDeleteSaveAd) {
          trackDeleteSaveAd();
        } else {
          pushToDataLayer({
            event: 'delete_saved_ad',
          });
        }
      }
      setIsAdSaved(!isSaved);
    }
  }

  async function handleRequest(savedStatus: boolean) {
    if (isJato && jatoPayload) {
      try {
        const value = savedStatus
          ? accountsApi.saveUnsaveNewCardAd(
              false,
              jatoPayload.make,
              jatoPayload.model,
              jatoPayload.bodyType,
              formatAuthorizationHeader(accessToken),
            )
          : accountsApi.saveUnsaveNewCardAd(
              true,
              jatoPayload.make,
              jatoPayload.model,
              jatoPayload.bodyType,
              formatAuthorizationHeader(accessToken),
            );
        return value;
      } catch (_error) {
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
        return false;
      }
    } else {
      try {
        const { status } = savedStatus
          ? await accountsApi.unsaveAd(
              id,
              formatAuthorizationHeader(accessToken),
            )
          : await accountsApi.saveAd(
              id,
              formatAuthorizationHeader(accessToken),
            );
        return status === StatusCode.SuccessOK;
      } catch (_error) {
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
        return false;
      }
    }
  }

  let tags = [];

  if (wanted) {
    tags.push(<WantedTag />);
  } else {
    if (deliveryAvailable) {
      tags.push(<DeliveryAvailableTag />);
    }

    if (cardPayments && cardPayments[0].acceptPayment) {
      tags.push(<CardPaymentTag />);
    }

    if (meritsV2) {
      const containsHistoryCheck = meritsV2.filter(
        (merit) => merit.field === 'historyChecked',
      );
      if (containsHistoryCheck.length === 1) {
        tags.push(<HistoryCheckTag />);
      }

      const containsWarranty = meritsV2.filter(
        (merit) => merit.field === 'warranty',
      );
      if (containsWarranty.length === 1) {
        tags.push(<WarrantyTag />);
      }
    }

    if (specialOffer) {
      tags.push(<Tag text={specialOffer} textColor="GREY_DARK" />);
    }

    if (greenlightVerified) {
      tags.push(<GreenLightTag />);
    }

    if (manufacturerApprovedBadge) {
      tags.push(
        <ManufacturerApproved
          manufacturer={manufacturerApprovedBadge.manufacturer ?? ''}
        />,
      );
    }

    if (herdwatchVerified) {
      tags.push(<HerdwatchTag />);
    }
  }

  const photo = photos && photos[0].small2;

  let injectedKeyInfo;

  if (isGrid) {
    injectedKeyInfo = [];
    if (age) injectedKeyInfo.push(age);
    if (county) {
      injectedKeyInfo.push(county);
    }
    if (sectionDisplayName && !isMobile)
      injectedKeyInfo.push(sectionDisplayName);
  } else {
    injectedKeyInfo = [...(keyInfo || [])];
    if (age) injectedKeyInfo.push(age);
    if (county) {
      if (countyTown) {
        injectedKeyInfo.push(`${countyTown}, ${county}`);
      } else {
        injectedKeyInfo.push(county);
      }
    }
    if (sectionDisplayName) injectedKeyInfo.push(sectionDisplayName);
  }

  return (
    <Card
      title={header}
      imgSrc={photo}
      imgSrcWebp={(photos && photos[0].small2Webp) || undefined}
      noImgSrc={`${cdnUrl}/images/fallbacks/card-no-image.svg`}
      oldPrice={(oldPriceView?.showOldPrice && oldPriceView.oldPrice) || ''}
      keyInfoList={injectedKeyInfo}
      info={infoItems}
      onSave={hideSave ? undefined : onSave}
      userSaved={isSaved}
      highlight={highlight}
      highlightText={highlightText}
      header={{
        title: dealer?.name,
        subTitle: renderSubtitle ? (
          <>{renderSubtitle}</>
        ) : (
          dealer?.franchiseType
        ),
        logo: dealer?.logo?.small,
      }}
      tags={isGrid ? [] : tags}
      imgAlt={imageAlt}
      priceOnRequest={priceOnRequest}
      renderAdditionalHeaderInfo={renderAdditionalHeaderInfo}
      shouldTruncateHeader={shouldTruncateHeader}
      smallTruncationHeaderWidth={smallTruncationHeaderWidth}
      {...rest}
    />
  );
}

export { CadCard };
